import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

const DEFAULT_IDLE_MS = 30 * 60 * 1000; // 30min
const SENSITIVE_IDLE_MS = 15 * 60 * 1000; // 15min em rotas críticas
const WARNING_BEFORE_MS = 5 * 60 * 1000;  // toast 5min antes
const SENSITIVE_PREFIXES = ['/account/security', '/admin/billing', '/admin/security'];

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange',
];

/**
 * Faz signOut automático após inatividade.
 * - 30min em rotas normais
 * - 15min em /account/security, /admin/billing, /admin/security
 * - aviso via toast 5min antes do logout
 *
 * Montar uma única vez (ex: dentro de AuthProvider em App.tsx).
 */
export function useIdleLogout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;

    const isSensitive = SENSITIVE_PREFIXES.some((p) => location.pathname.startsWith(p));
    const idleMs = isSensitive ? SENSITIVE_IDLE_MS : DEFAULT_IDLE_MS;
    const warnMs = Math.max(idleMs - WARNING_BEFORE_MS, idleMs / 2);

    const clearTimers = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };

    const reset = () => {
      clearTimers();
      warnTimer.current = setTimeout(() => {
        toast.warning('Você será desconectado por inatividade em 5 minutos.', {
          duration: 8000,
        });
      }, warnMs);
      logoutTimer.current = setTimeout(async () => {
        toast.info('Sessão encerrada por inatividade.');
        await signOut();
      }, idleMs);
    };

    reset();
    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, reset, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, reset));
    };
  }, [user, location.pathname, signOut]);
}
